<template>
  <span class="d-inline-flex align-center ml-1">
    <v-btn
      icon
      small
      :title="$t('panelPlanning.help.buttonTitle')"
      aria-label="help"
      @click="open = true"
    >
      <v-icon small>mdi-help-circle-outline</v-icon>
    </v-btn>

    <v-dialog
      v-model="open"
      :max-width="modalLayout.MD"
      v-bind="modalPolicy.P1_DISMISSIBLE"
      scrollable
    >
      <v-card class="panel-planning-help text-left">
        <v-card-title class="panel-planning-help__title text-h6">
          <v-icon small class="panel-planning-help__title-icon">mdi-book-open-page-variant</v-icon>
          <span>{{ $t('panelPlanning.help.dialogTitle') }}</span>
        </v-card-title>

        <v-card-text class="pt-0 text-left">
          <p class="text-body-2 text--secondary mb-4 text-left">
            {{ $t('panelPlanning.help.intro') }}
          </p>

          <v-expansion-panels accordion flat class="panel-planning-help-panels">
            <v-expansion-panel v-for="section in sections" :key="section.id">
              <v-expansion-panel-header class="panel-planning-help__section-header font-weight-medium">
                <div class="panel-planning-help__header-inner">
                  <v-icon small class="panel-planning-help__section-icon">
                    {{ section.icon }}
                  </v-icon>
                  <span class="panel-planning-help__section-title">{{ $t(section.titleKey) }}</span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="text-left">
                <ul class="help-list text-body-2 pl-0 mb-0">
                  <li v-for="itemKey in section.itemKeys" :key="itemKey">
                    {{ $t(itemKey) }}
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-subheader class="px-0 mt-2 justify-start text-left">
            {{ $t('panelPlanning.help.shortcutsTitle') }}
          </v-subheader>
          <v-simple-table dense class="help-shortcuts-table text-left">
            <tbody>
              <tr v-for="row in shortcutRows" :key="row.actionKey">
                <td class="font-weight-medium text-no-wrap">
                  {{ $t(row.keysKey) }}
                </td>
                <td>{{ $t(row.actionKey) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="open = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import { MODAL_CLOSE_POLICY, MODAL_MAX_WIDTH } from '@/constants/modalLayout';

const HELP_SECTIONS = [
  {
    id: 'structure',
    icon: 'mdi-file-tree',
    titleKey: 'panelPlanning.help.sectionStructureTitle',
    itemKeys: [
      'panelPlanning.help.sectionStructureItem1',
      'panelPlanning.help.sectionStructureItem2',
      'panelPlanning.help.sectionStructureItem3'
    ]
  },
  {
    id: 'start',
    icon: 'mdi-map-marker-radius',
    titleKey: 'panelPlanning.help.sectionStartTitle',
    itemKeys: [
      'panelPlanning.help.sectionStartItem1',
      'panelPlanning.help.sectionStartItem2',
      'panelPlanning.help.sectionStartItem3',
      'panelPlanning.help.sectionStartItem4'
    ]
  },
  {
    id: 'hierarchy',
    icon: 'mdi-chart-tree',
    titleKey: 'panelPlanning.help.sectionHierarchyTitle',
    itemKeys: [
      'panelPlanning.help.sectionHierarchyItem1',
      'panelPlanning.help.sectionHierarchyItem2',
      'panelPlanning.help.sectionHierarchyItem3',
      'panelPlanning.help.sectionHierarchyItem4'
    ]
  },
  {
    id: 'draw',
    icon: 'mdi-vector-square',
    titleKey: 'panelPlanning.help.sectionDrawTitle',
    itemKeys: [
      'panelPlanning.help.sectionDrawItem1',
      'panelPlanning.help.sectionDrawItem2',
      'panelPlanning.help.sectionDrawItem3',
      'panelPlanning.help.sectionDrawItem4'
    ]
  },
  {
    id: 'split',
    icon: 'mdi-grid',
    titleKey: 'panelPlanning.splitTerminologyTitle',
    itemKeys: [
      'panelPlanning.splitTerminologyIntro',
      'panelPlanning.splitTerminologyVertical',
      'panelPlanning.splitTerminologyHorizontal',
      'panelPlanning.splitTerminologyPolygon'
    ]
  },
  {
    id: 'auto',
    icon: 'mdi-lightning-bolt',
    titleKey: 'panelPlanning.help.sectionAutoTitle',
    itemKeys: [
      'panelPlanning.help.sectionAutoItem1',
      'panelPlanning.help.sectionAutoItem2',
      'panelPlanning.help.sectionAutoItem3'
    ]
  },
  {
    id: 'select',
    icon: 'mdi-cursor-default-click',
    titleKey: 'panelPlanning.help.sectionSelectTitle',
    itemKeys: [
      'panelPlanning.help.sectionSelectItem1',
      'panelPlanning.help.sectionSelectItem2',
      'panelPlanning.help.sectionSelectItem3',
      'panelPlanning.help.sectionSelectItem4'
    ]
  },
  {
    id: 'moveCopy',
    icon: 'mdi-content-copy',
    titleKey: 'panelPlanning.help.sectionMoveCopyTitle',
    itemKeys: [
      'panelPlanning.help.sectionMoveCopyItem1',
      'panelPlanning.help.sectionMoveCopyItem2',
      'panelPlanning.help.sectionMoveCopyItem3',
      'panelPlanning.help.sectionMoveCopyItem4'
    ]
  },
  {
    id: 'tree',
    icon: 'mdi-folder-edit',
    titleKey: 'panelPlanning.help.sectionTreeTitle',
    itemKeys: [
      'panelPlanning.help.sectionTreeItem1',
      'panelPlanning.help.sectionTreeItem2',
      'panelPlanning.help.sectionTreeItem3',
      'panelPlanning.help.sectionTreeItem4',
      'panelPlanning.help.sectionTreeItem5',
      'panelPlanning.help.sectionTreeItem6'
    ]
  }
];

const SHORTCUT_ROWS = [
  {
    keysKey: 'panelPlanning.help.shortcutRotateKeys',
    actionKey: 'panelPlanning.help.shortcutRotateAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutMultiSelectKeys',
    actionKey: 'panelPlanning.help.shortcutMultiSelectAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutModuleSelectKeys',
    actionKey: 'panelPlanning.help.shortcutModuleSelectAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutCopyKeys',
    actionKey: 'panelPlanning.help.shortcutCopyAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutCopyDragKeys',
    actionKey: 'panelPlanning.help.shortcutCopyDragAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutEscapeKeys',
    actionKey: 'panelPlanning.help.shortcutEscapeAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutMultiCopyKeys',
    actionKey: 'panelPlanning.help.shortcutMultiCopyAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutTreeNavKeys',
    actionKey: 'panelPlanning.help.shortcutTreeNavAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutTreeSelectKeys',
    actionKey: 'panelPlanning.help.shortcutTreeSelectAction'
  },
  {
    keysKey: 'panelPlanning.help.shortcutSiteSearchKeys',
    actionKey: 'panelPlanning.help.shortcutSiteSearchAction'
  }
];

export default {
  name: 'PanelPlanningHelpDialog',
  data() {
    return {
      open: false,
      modalLayout: MODAL_MAX_WIDTH,
      modalPolicy: MODAL_CLOSE_POLICY,
      sections: HELP_SECTIONS,
      shortcutRows: SHORTCUT_ROWS
    };
  }
};
</script>

<style scoped>
.panel-planning-help__title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
}

.panel-planning-help__title-icon {
  flex-shrink: 0;
  margin-right: 8px;
}

.panel-planning-help-panels >>> .v-expansion-panel-header {
  justify-content: flex-start;
  text-align: left;
}

.panel-planning-help__header-inner {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.panel-planning-help__section-icon {
  flex-shrink: 0;
  margin-right: 8px;
}

.panel-planning-help__section-title {
  text-align: left;
}

.panel-planning-help-panels >>> .v-expansion-panel-header__icon {
  margin-left: auto;
  flex-shrink: 0;
}

.panel-planning-help-panels >>> .v-expansion-panel-content__wrap {
  text-align: left;
}

.help-list {
  list-style: none;
}

.help-list li {
  position: relative;
  padding-left: 1rem;
  margin-bottom: 0.5rem;
}

.help-list li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: rgba(0, 0, 0, 0.54);
}

.help-shortcuts-table td:first-child {
  width: 40%;
  vertical-align: top;
}
</style>
